import { useCallback, useMemo } from "react"
import { useClipboardBackend } from "./backends"
import type { BackendConfig } from "./backends/useClipboardBackend"
import {
  ClearOptions,
  Clip,
  ClipboardAPI,
  CopyOptions,
  GetClipOptions
} from "./types"
import { useBackendClips } from "./utils"

/**
 * A flexible clipboard hook that can be used with multiple backends.
 *
 * @param config - (optional) Configuration object with optional backends and object_type.
 *   - backends?: BackendConfig[] - Backend configs to try in order (default: ['firebase', 'localStorage', 'memory'])
 *   - object_type?: string - Default object_type for all operations (can be overridden at method level)
 * @returns Clipboard operations: { clear, copy, getClip }
 */
const useClipboard = <T extends { object_type?: string } = {}>(
  config?: T & { backends?: BackendConfig[] }
): ClipboardAPI<T> => {
  const { backends = [], object_type: defaultObjectType } = config ?? {}

  // Get the active backend with options
  const activeBackend = useClipboardBackend(backends)
  const clips = useBackendClips(activeBackend)

  // --- Clip filtering helpers (supports both hook-scoped and method-scoped object_type) ---
  const _filterClips = useCallback(
    ({
      object_id,
      object_label,
      object_type
    }: {
      object_id?: string
      object_label?: string
      object_type: string
    }): Clip[] => {
      const filteredClips = clips.filter(
        (clip) =>
          clip.object_type === object_type &&
          (!object_id || clip.object_id === object_id) &&
          (!object_label || clip.object_label === object_label)
      )
      return filteredClips
    },
    [clips]
  )

  const _removeClipsOfType = useCallback(
    (objectType: string): Clip[] => {
      return clips.filter((clip) => clip.object_type !== objectType)
    },
    [clips]
  )

  // --- Save logic ---
  const _save = useCallback(
    async (nextClips: Clip[] | null = null): Promise<void> => {
      if (!activeBackend) {
        console.warn("[Clipboard] No available clipboard backend for save")
        return
      }
      const clipsToSave = nextClips || clips
      try {
        await activeBackend.saveClips(clipsToSave)
        // setClips is handled by subscription in useBackendClips
      } catch (error) {
        console.warn(`[Clipboard] Failed to save clips:`, error)
      }
    },
    [activeBackend, clips]
  )

  // --- Shared validation ---
  type MethodOptions = CopyOptions | GetClipOptions | ClearOptions

  const validateType = (
    options: MethodOptions | undefined,
    operation: string
  ): string => {
    const objectType = options?.object_type || defaultObjectType
    if (!objectType) {
      throw new Error(
        `[Clipboard] object_type is required for ${operation} operation (neither hook-scoped nor method-scoped)`
      )
    }
    if (
      options?.object_type &&
      defaultObjectType &&
      options.object_type !== defaultObjectType
    ) {
      throw new Error(
        `[Clipboard] object_type is not allowed for ${operation} operation (hook is already scoped to ${defaultObjectType})`
      )
    }
    return objectType
  }

  // --- API methods (support both hook-scoped and method-scoped object_type) ---
  const clear: ClipboardAPI<T>["clear"] = (options?: ClearOptions) => {
    const objectType = validateType(options, "clear")
    const remainingClips = _removeClipsOfType(objectType)
    return _save(remainingClips)
  }

  const getClip: ClipboardAPI<T>["getClip"] = (options?: GetClipOptions) => {
    const objectType = validateType(options, "getClip")
    const filteredClips = _filterClips({
      object_id: options?.object_id,
      object_type: objectType
    })
    return filteredClips[filteredClips.length - 1]
  }

  const copy: ClipboardAPI<T>["copy"] = (
    options: CopyOptions,
    data: Record<string, any> = {}
  ) => {
    const objectType = validateType(options, "copy")
    const { object_id, object_label } = options
    if (!object_id || !object_label)
      throw new Error(
        "[Clipboard] object_id and object_label are required for copy"
      )
    const clip: Clip = {
      object_type: objectType,
      object_id,
      object_label,
      data
    }
    const nextClips = _removeClipsOfType(objectType).concat([clip])
    return _save(nextClips)
  }

  // Memoize the returned API to prevent infinite re-renders
  const api = useMemo(
    () => ({
      clear,
      copy,
      getClip
    }),
    [clear, copy, getClip]
  )

  return api as ClipboardAPI<T>
}

export default useClipboard
